import { createPortal } from "react-dom";
import { Box, Typography } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { CloudOff, CloudUpload, Check } from "lucide-react";
import type { AutoSaveStatus } from "../hooks/useAutoSave";

const EASE = [0.4, 0, 0.2, 1] as const;

const STATUS_CONFIG = {
    saving: { icon: CloudUpload, label: "Saving…", color: "neutral.500" as const },
    saved: { icon: Check, label: "Saved", color: "success.500" as const },
    error: { icon: CloudOff, label: "Save failed", color: "danger.500" as const },
} as const;

function StatusIcon({ status }: { status: Exclude<AutoSaveStatus, "idle"> }) {
    const Icon = STATUS_CONFIG[status].icon;

    if (status === "saving") {
        return (
            <motion.div
                animate={{ y: [0, -3, 0], opacity: [0.65, 1, 0.65] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                style={{ display: "flex", alignItems: "center" }}
            >
                <Icon size={14} />
            </motion.div>
        );
    }

    if (status === "saved") {
        return (
            <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 420, damping: 22 }}
                style={{ display: "flex", alignItems: "center" }}
            >
                <Icon size={14} strokeWidth={2.5} />
            </motion.div>
        );
    }

    return (
        <motion.div
            animate={{ x: [0, -2, 2, -1, 1, 0] }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            style={{ display: "flex", alignItems: "center" }}
        >
            <Icon size={14} />
        </motion.div>
    );
}

export function AutoSaveStatusIndicator({ status }: { status: AutoSaveStatus }) {
    const isVisible = status !== "idle";
    const activeStatus = status === "idle" ? "saving" : status;
    const config = STATUS_CONFIG[activeStatus];

    return createPortal(
        <Box
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: {
                    xs: "calc(64px + env(safe-area-inset-bottom, 0px))",
                    sm: 24,
                },
                zIndex: 1200,
                display: "flex",
                justifyContent: "center",
                px: 2,
                pointerEvents: "none",
            }}
        >
            <AnimatePresence>
                {isVisible && (
                    <motion.div
                        key="autosave-chip"
                        initial={{ opacity: 0, y: 12, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.28, ease: EASE }}
                        style={{ pointerEvents: "auto" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: "4px",
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.surface",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                backdropFilter: "blur(12px)",
                                overflow: "hidden",
                            }}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={status}
                                    initial={{ opacity: 0, x: 8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -8 }}
                                    transition={{ duration: 0.24, ease: EASE }}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: config.color,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <StatusIcon status={activeStatus} />
                                    </Box>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            color: config.color,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {config.label}
                                    </Typography>
                                </motion.div>
                            </AnimatePresence>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>,
        document.body,
    );
}
